import { FieldDataByFieldNameType } from '@app-store'
import { FieldValues } from 'react-hook-form'

export function pushUnique(arr: string[], value: string) {
  if (!arr.includes(value)) arr.push(value)
}

export function extractNestedSelect(fieldDef: FieldValues) {
  if (!fieldDef || typeof fieldDef !== 'object') return []
  const nested = fieldDef.added_fields || fieldDef.addedFields // support both key styles just in case
  if (!nested || typeof nested !== 'object') return []
  return Object.keys(nested)
}

export function buildQuery(
  entityId: string,
  rows: {
    isAssociated: boolean
    addedFields: FieldDataByFieldNameType
    associatedPath?: string
  }[],
  options: FieldValues = {}
) {
  const { page = 0, size = 25 } = options

  if (!Array.isArray(rows)) {
    throw new Error('Root JSON is not an array')
  }

  const topSelect: string[] = []
  const related: FieldValues = {}

  function ensureRelated(key: string) {
    if (!related[key]) related[key] = { select: [] }
    return related[key]
  }

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const isAssociated = !!row.isAssociated
    const addedFields = row.addedFields
    if (!addedFields || typeof addedFields !== 'object') continue

    let associatedContainer = null
    if (isAssociated) {
      const assocPath = row.associatedPath
      if (assocPath) {
        associatedContainer = ensureRelated(assocPath)
        pushUnique(topSelect, assocPath)
      }
    }

    for (const [fieldName, fieldDef] of Object.entries(addedFields)) {
      if (!fieldName) continue

      const uiType = fieldDef?.ui_data_type
      const isSingleLookup = uiType === 'single lookup'
      const isMultiLookup = uiType === 'multi lookup'

      if (isMultiLookup) continue

      if (isAssociated && associatedContainer) {
        pushUnique(associatedContainer.select, fieldName)
      } else if (!isAssociated) {
        if (!isSingleLookup) {
          pushUnique(topSelect, fieldName)
        }
      }

      if (isSingleLookup) {
        const rel = ensureRelated(fieldName)
        const nestedSelect = extractNestedSelect(fieldDef as FieldValues)
        for (const nestedField of nestedSelect) {
          pushUnique(rel.select, nestedField)
        }
        pushUnique(topSelect, fieldName)
      }
    }
  }

  const result = {
    entity_id: entityId,
    select: topSelect,
    page,
    size,
    related_entities: related,
    filters: [
      {
        criteria: [
          // {
          //   values: [recordId],
          //   operation: 'EQUALS',
          //   field_name: 'id',
          // },
        ],
      },
    ],
  }
  return result
}

/**
 * Extended variant that supports an optional parentKey.
 * Behavior when parentKey is provided:
 *  - Ensures parentKey is the ONLY top level field in result.select
 *  - All other previously selected top-level fields become part of
 *    related_entities[parentKey].select
 *  - FieldValues related_entities that belonged to those fields are moved under
 *    related_entities[parentKey].related_entities preserving their structure
 *  - If parentKey already had a related entity entry it is merged
 * If parentKey not supplied, falls back to standard buildQuery output.
 *
 * options: { page?, size?, parentKey? }
 */
export function buildQueryWithParent(
  entityId: string,
  rows: {
    isAssociated: boolean
    addedFields: FieldDataByFieldNameType
    associatedPath?: string
  }[],
  options: FieldValues = {},
  function_parameters?: FieldValues
) {
  const { parentKey, page = 0, size = 25 } = options || {}
  // First build the normal structure

  const base = buildQuery(entityId, rows, { page, size })

  if (!parentKey) return base

  // Ensure parentKey is in original select (if not, we still add it)
  if (!base.select.includes(parentKey)) {
    base.select.push(parentKey)
  }

  const oldRelated = base.related_entities || {}
  const parentEntry: FieldValues = {
    select: [],
    related_entities: {},
  }

  // Collect all top-level fields except parentKey to move under the parent
  const fieldsToNest = base.select.filter((f) => f !== parentKey)

  for (const field of fieldsToNest) {
    // Add the field as a selected field inside parent entity
    pushUnique(parentEntry.select, field)

    // If that field had its own related entity (single lookup originally),
    // move it under parent.related_entities[field]
    if (oldRelated[field]) {
      parentEntry.related_entities[field] = oldRelated[field]
      delete oldRelated[field]
    }
  }

  // If parentKey itself had a related entry already, merge it
  if (oldRelated[parentKey]) {
    const existingParent = oldRelated[parentKey]

    // Merge selects
    for (const s of existingParent.select || []) {
      pushUnique(parentEntry.select, s)
    }

    // Merge nested related_entities
    if (existingParent.related_entities) {
      parentEntry.related_entities = {
        ...existingParent.related_entities,
        ...parentEntry.related_entities,
      }
    }
    delete oldRelated[parentKey]
  }

  // Build new related_entities object: parent only
  const newRelated = {
    [parentKey]: parentEntry,
  }

  // Return transformed structure
  return {
    ...base,
    select: [parentKey],
    related_entities: newRelated,
    ...(function_parameters ? { function_parameters } : {}),
  }
}

export function denormalizeLookups(normalized: FieldValues): FieldValues {
  const isPlainObj = (v: FieldValues) =>
    v && typeof v === 'object' && !Array.isArray(v)

  if (Array.isArray(normalized)) {
    return normalized.map((item) => denormalizeLookups(item))
  }

  if (!isPlainObj(normalized)) return normalized

  const lookupData = normalized.__singlelookup__data || {}
  const result: FieldValues = {}
  let rootChanged = false

  function pruneUndefined(obj: FieldValues) {
    if (!isPlainObj(obj)) return
    for (const k of Object.keys(obj)) {
      const val = obj[k]
      if (val === undefined) {
        delete obj[k]
        continue
      }
      if (isPlainObj(val)) {
        pruneUndefined(val)
        // After pruning, if child became removable empty, let final sweep handle it
      } else if (Array.isArray(val)) {
        obj[k] = val
          .filter((item) => item !== undefined)
          .map((item) => {
            if (isPlainObj(item)) {
              pruneUndefined(item)
              return item
            }
            return item
          })
        if ((obj[k] as FieldValues[]).length === 0) {
          // leave empty array if meaningful? Remove if you wish; currently keep
        }
      }
    }
  }

  function isEmptyObjectForRemoval(obj: FieldValues) {
    if (!isPlainObj(obj)) return false
    // If the object has an id, we always keep it (even if other keys got pruned)
    if (Object.prototype.hasOwnProperty.call(obj, 'id')) return false
    const keys = Object.keys(obj)
    if (keys.length === 0) return true
    // Removable if the only remaining key is _state
    return keys.every((k) => k === '_state')
  }

  function mergeSingleLookup(
    mainVal: FieldValues,
    metaVal: FieldValues | undefined
  ) {
    const merged: FieldValues = { id: mainVal.id }
    if (!metaVal) {
      return merged
    }
    if (isPlainObj(metaVal)) {
      for (const mk of Object.keys(metaVal)) {
        if (mk === '__singlelookup__data') continue
        const mv = metaVal[mk]
        if (mv === undefined) continue // skip undefined meta properties
        merged[mk] = mv
      }
    }

    // Remove any undefined keys that slipped in
    pruneUndefined(merged)

    const metaAdds = Object.keys(merged).some(
      (k) => k !== 'id' && k !== '_state'
    )
    if (metaAdds) {
      merged._state = merged._state || mainVal._state || 'CHANGED'
      rootChanged = true
    } else if (mainVal._state) {
      merged._state = mainVal._state
    }

    const nestedMeta = metaVal?.__singlelookup__data
    if (nestedMeta && isPlainObj(nestedMeta)) {
      for (const nestedKey of Object.keys(nestedMeta)) {
        const childMeta = nestedMeta[nestedKey]
        const childNormalized = {
          [nestedKey]:
            merged[nestedKey] && isPlainObj(merged[nestedKey])
              ? merged[nestedKey]
              : childMeta?.id
              ? { id: childMeta.id }
              : childMeta,
          __singlelookup__data: { [nestedKey]: childMeta },
        }
        const den = denormalizeLookups(childNormalized)[nestedKey]
        if (den !== undefined) merged[nestedKey] = den
      }
    }

    pruneUndefined(merged)
    return merged
  }

  for (const key of Object.keys(normalized)) {
    if (key === '__singlelookup__data') continue
    const mainVal = normalized[key]
    if (mainVal === undefined) continue // skip undefined main values entirely (but keep null)
    const metaVal = lookupData[key]

    if (Array.isArray(mainVal)) {
      if (Array.isArray(metaVal)) {
        const arr = mainVal
          .map((item, idx) => {
            if (item === undefined) return undefined // skip undefined array item
            const itemMeta = metaVal[idx] ? metaVal[idx] : undefined
            if (isPlainObj(item) && 'id' in item) {
              const merged = mergeSingleLookup(item, itemMeta)
              return isEmptyObjectForRemoval(merged) ? undefined : merged
            }
            const den = denormalizeLookups(item)
            if (isPlainObj(den)) {
              den._state = 'ADDED'
              if (isEmptyObjectForRemoval(den)) return undefined
            }
            return den
          })
          .filter((v) => v !== undefined)
        if (arr.length > 0) result[key] = arr // only assign if non-empty
      } else {
        const arr = mainVal
          .map((item) => {
            if (item === undefined) return undefined
            if (isPlainObj(item) && 'id' in item) {
              let itemMeta: FieldValues | undefined = undefined
              if (metaVal) {
                if (isPlainObj(metaVal) && metaVal[item.id]) {
                  itemMeta = metaVal[item.id]
                } else {
                  itemMeta = metaVal as FieldValues
                }
              }
              const merged = mergeSingleLookup(item, itemMeta)
              return isEmptyObjectForRemoval(merged) ? undefined : merged
            }
            const den = denormalizeLookups(item)
            if (isPlainObj(den)) {
              den._state = 'ADDED'
              if (isEmptyObjectForRemoval(den)) return undefined
            }
            return den
          })
          .filter((v) => v !== undefined)
        if (arr.length > 0) result[key] = arr
      }
      continue
    }

    if (isPlainObj(mainVal) && !('id' in mainVal) && !metaVal) {
      const den = denormalizeLookups(mainVal)
      if (isPlainObj(den) && !den._state) {
        den._state = 'ADDED'
      }
      if (!isEmptyObjectForRemoval(den)) {
        result[key] = den
      }
      continue
    }

    if (isPlainObj(mainVal) && 'id' in mainVal) {
      const merged = mergeSingleLookup(mainVal, metaVal)
      if (!isEmptyObjectForRemoval(merged)) {
        result[key] = merged
      }
      continue
    }

    // primitive value but not undefined
    result[key] = mainVal
  }

  for (const key of Object.keys(lookupData)) {
    if (result[key] !== undefined) continue
    const metaVal = lookupData[key]
    if (metaVal === undefined) continue // skip only undefined, allow null

    if (Array.isArray(metaVal)) {
      const arr = metaVal
        .map((m) => {
          if (m === undefined) return undefined
          if (isPlainObj(m) && 'id' in m) {
            const merged = mergeSingleLookup(
              { id: (m as FieldValues).id },
              m as FieldValues
            )
            return isEmptyObjectForRemoval(merged) ? undefined : merged
          }
          const den = denormalizeLookups(m)
          if (isPlainObj(den)) {
            den._state = 'ADDED'
            if (isEmptyObjectForRemoval(den)) return undefined
          }
          return den
        })
        .filter((v) => v !== undefined)
      if (arr.length > 0) {
        result[key] = arr
        rootChanged = true
      }
      continue
    }

    if (isPlainObj(metaVal) && 'id' in metaVal) {
      const merged = mergeSingleLookup(
        { id: (metaVal as FieldValues).id },
        metaVal as FieldValues
      )
      if (!isEmptyObjectForRemoval(merged)) {
        result[key] = merged
        rootChanged = true
      }
    } else if (isPlainObj(metaVal)) {
      const clone = { ...(metaVal as FieldValues) }
      clone._state = 'ADDED'
      if (!isEmptyObjectForRemoval(clone)) {
        result[key] = clone
        rootChanged = true
      }
    } else {
      // primitive non-undefined
      result[key] = metaVal
      rootChanged = true
    }
  }

  // Prune undefineds before emptiness sweep
  pruneUndefined(result)

  // Final sweep: remove any keys that became empty (_state only or nothing)
  for (const k of Object.keys(result)) {
    const v = result[k]
    if (isEmptyObjectForRemoval(v)) {
      delete result[k]
    }
  }

  if (rootChanged && !result._state) result._state = 'CHANGED'
  return result
}

export function normalizeLookups(obj: FieldValues): FieldValues {
  const result: FieldValues = {}
  const lookupData: FieldValues = {}

  const hasCategoryFlag = (o: FieldValues) =>
    o &&
    typeof o === 'object' &&
    (Object.prototype.hasOwnProperty.call(o, 'category') ||
      Object.prototype.hasOwnProperty.call(o, 'catagory'))

  for (const key in obj) {
    const value = obj[key]

    // Arrays: move entirely into __singlelookup__data, normalizing each element
    if (Array.isArray(value)) {
      lookupData[key] = value.map((item) =>
        item && typeof item === 'object' ? normalizeLookups(item) : item
      )
      continue
    }

    // Single lookup object (non-array)
    if (value && typeof value === 'object' && 'id' in value) {
      const isCategoryType = hasCategoryFlag(value)
      if (isCategoryType) {
        result[key] = { id: value.id }
        continue
      }

      result[key] = { id: value.id }

      const rest = { ...value }
      delete rest.id

      const singleLookupEntry: FieldValues = {}
      const nestedSingleLookup: FieldValues = {}

      for (const subKey in rest) {
        const subVal = rest[subKey]

        // Nested array inside a lookup object
        if (Array.isArray(subVal)) {
          singleLookupEntry[subKey] = subVal.map((item) =>
            item && typeof item === 'object' ? normalizeLookups(item) : item
          )
          continue
        }

        if (subVal && typeof subVal === 'object' && 'id' in subVal) {
          const subHasCategory = hasCategoryFlag(subVal) ? true : false

          if (subHasCategory) {
            singleLookupEntry[subKey] = { id: subVal.id }
            continue
          }

          singleLookupEntry[subKey] = { id: subVal.id }

          // Normalize nested lookup to capture its meta
          const nestedResult = normalizeLookups({ [subKey]: subVal })
          if (
            nestedResult.__singlelookup__data &&
            nestedResult.__singlelookup__data[subKey]
          ) {
            const clone = { ...nestedResult.__singlelookup__data[subKey] }
            delete clone.id
            nestedSingleLookup[subKey] = clone
          }
        } else {
          singleLookupEntry[subKey] = subVal
        }
      }

      if (Object.keys(nestedSingleLookup).length > 0) {
        singleLookupEntry.__singlelookup__data = nestedSingleLookup
      }

      lookupData[key] = singleLookupEntry
      continue
    }

    // Primitive / passthrough
    result[key] = value
  }

  return {
    ...result,
    __singlelookup__data: lookupData,
  }
}

export const compareAndMark = (
  oldObj: FieldValues,
  newObj: FieldValues
): { changed: boolean; value: FieldValues } => {
  const safeOld = oldObj && typeof oldObj === 'object' ? oldObj : {}
  const safeNew = newObj && typeof newObj === 'object' ? newObj : {}
  const result: FieldValues = {}
  let FieldValuesChange = false

  const isObject = (v: FieldValues) =>
    v && typeof v === 'object' && !Array.isArray(v)

  for (const key of Object.keys(safeNew)) {
    const newVal = safeNew[key]
    const hasOld = Object.prototype.hasOwnProperty.call(safeOld, key)
    const oldVal = safeOld[key]

    if (isObject(newVal)) {
      const { changed, value } = compareAndMark(oldVal, newVal)
      result[key] = value
      if (!hasOld || changed) {
        result[`${key}_state`] = 'changed'
        FieldValuesChange = true
      }
    } else {
      const primitiveChanged =
        !hasOld ||
        (Array.isArray(newVal)
          ? JSON.stringify(oldVal) !== JSON.stringify(newVal)
          : oldVal !== newVal)

      result[key] = newVal
      if (primitiveChanged) {
        result[`${key}_state`] = 'changed'
        FieldValuesChange = true
      }
    }
  }

  return { changed: FieldValuesChange, value: result }
}

export function diffObjectsWithState(
  oldData: FieldValues = {},
  newData: FieldValues = {}
) {
  const isPlainObj = (v: FieldValues) =>
    v && typeof v === 'object' && !Array.isArray(v)

  const result: FieldValues = {}
  let FieldValuesChange = false

  const allKeys = new Set([
    ...Object.keys(oldData || {}),
    ...Object.keys(newData || {}),
  ])

  for (const key of allKeys) {
    const inOld = Object.prototype.hasOwnProperty.call(oldData, key)
    const inNew = Object.prototype.hasOwnProperty.call(newData, key)
    const oldVal = oldData[key]
    const newVal = newData[key]

    // Removed (key gone entirely)
    if (inOld && !inNew) {
      if (isPlainObj(oldVal)) {
        result[key] = { ...oldVal, _state: 'REMOVED' }
        FieldValuesChange = true
      }
      continue
    }

    // Added (key did not exist before)
    if (!inOld && inNew) {
      if (isPlainObj(newVal)) {
        result[key] = { ...newVal, _state: 'ADDED' }
        FieldValuesChange = true
      } else {
        // primitive addition: include as-is
        result[key] = newVal
        // treat as change so parent can know
        FieldValuesChange = true
      }
      continue
    }

    // Present in both (or old present as null)
    if (inOld && inNew) {
      const oldIsObj = isPlainObj(oldVal)
      const newIsObj = isPlainObj(newVal)

      // Rule: old null, new object => Added
      if (oldVal === null && newIsObj) {
        result[key] = { ...newVal, _state: 'ADDED' }
        FieldValuesChange = true
        continue
      }

      // Rule: old object, new null => removed
      if (oldIsObj && newVal === null) {
        result[key] = { ...oldVal, _state: 'ADDED' }
        FieldValuesChange = true
        continue
      }

      // Rule: old null, new primitive (not object) => skip (do not include)
      if (oldVal === null && !newIsObj) {
        continue
      }

      // Rule: new is primitive (include as-is)
      if (!newIsObj) {
        result[key] = newVal
        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          FieldValuesChange = true
        }
        continue
      }

      // Both objects
      if (oldIsObj && newIsObj) {
        if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
          result[key] = newVal
        } else {
          result[key] = { ...newVal, _state: 'CHANGED' }
          FieldValuesChange = true
        }
        continue
      }

      // Fallback (mixed types)
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        if (newIsObj) {
          result[key] = { ...newVal, _state: 'CHANGED' }
        } else {
          result[key] = newVal
        }
        FieldValuesChange = true
      } else {
        result[key] = newVal
      }
    }
  }

  if (FieldValuesChange) result._state = 'CHANGED'
  return result
}

type FieldValuesObject = Record<string, FieldValues>

export function mergeNestedObjects(
  target: FieldValuesObject = {},
  source: FieldValuesObject = {},
  keyToMerge = ''
): FieldValuesObject {
  const safeTarget = target || {}
  const safeSource = source || {}

  // If no keyToMerge provided, perform a plain deep merge of the two root objects
  if (!keyToMerge) {
    return deepMerge(safeTarget, safeSource)
  }

  // Otherwise merge inside __singlelookup__data[keyToMerge]
  const result: FieldValuesObject = { ...safeTarget }
  result.__singlelookup__data = result.__singlelookup__data || {}

  if (result.__singlelookup__data[keyToMerge]) {
    result.__singlelookup__data[keyToMerge] = deepMerge(
      result.__singlelookup__data[keyToMerge],
      safeSource
    )
  } else {
    result.__singlelookup__data[keyToMerge] = safeSource
  }

  return result
}

function deepMerge(
  target: FieldValuesObject = {},
  source: FieldValuesObject = {}
): FieldValuesObject {
  const output: FieldValuesObject = { ...target }

  for (const key in source) {
    const srcVal = source[key]
    const tgtVal = target[key]
    if (
      srcVal &&
      typeof srcVal === 'object' &&
      !Array.isArray(srcVal) &&
      !(srcVal instanceof Date)
    ) {
      output[key] = deepMerge(
        (tgtVal && typeof tgtVal === 'object'
          ? tgtVal
          : {}) as FieldValuesObject,
        srcVal
      )
    } else {
      output[key] = srcVal
    }
  }

  return output
}

export function calculateAnnualAmountFromFrequency(
  frequency: string,
  amount: number
): number {
  let multiplier = 1

  switch (frequency) {
    case 'DAILY':
      multiplier = 365
      break
    case 'WEEKLY':
      multiplier = 52
      break
    case 'BI-WEEKLY':
    case 'BIWEEKLY':
      multiplier = 1
      break
    case 'MONTHLY':
      multiplier = 12
      break
    case 'QUARTERLY':
      multiplier = 4
      break
    case 'SEMI-ANNUAL':
      multiplier = 2
      break
    case 'ANNUAL':
    case 'ANNUALLY':
      multiplier = 1
      break
    case 'EVERY OTHER WEEK':
      multiplier = 26
      break
    case 'TWICE MONTHLY':
      multiplier = 24
      break
    default:
      multiplier = 1
  }
  const result = amount * multiplier

  return Math.round(result * 100) / 100
}

// Usage:
// const merged = mergeNestedObjects(firstObject, secondObject, 'patient')
// console.log(JSON.stringify(merged, null, 2))
